import React, { useContext, useState, useEffect } from 'react'
import classNames from 'classnames'
import get from 'lodash/get'
import { useTranslation } from 'react-i18next'
import SideListLabel from './SideListLabel'
import getNameOfGqlQuery from 'helpers/getNameOfGqlQuery'
import { DataTable } from 'components/DataTable'
import { FilterContext } from '../context/FilterContext'
import { RefreshSideListContext } from '../context/RefreshSideListContext'
import { ReactComponent as Trashbin } from 'media/icons/trashbin.svg'
import { ReactComponent as Copy } from 'media/icons/copy-file.svg'
import { Checkbox } from 'components/forms'
import { StyledCheckbox, StyledColList } from '../styled'
import styles from 'style/app.module.scss'
import useLocale from 'hooks/useLocale'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'

const SideList = ({ xs = true, width, wide, fixSidebar = true, itemSize, datatableProps = {}, multiEdit = {}, commonData, listFetchPolicy, ...props }) => {
	const { history, getRouteById, selectRecord, mutations, listQueryRef, addNewRecord: { status, startNewRecord, startCopyRow } = {} } = props
	const { sectionData = {}, section, isNewRecord, recordId, location: { state = {} } } = props
	const isWideCol = get(sectionData, 'wideSideList', wide)
	const { setFilter, ...filters } = useContext(FilterContext)
	const { refreshSideList, setRefreshSideList } = useContext(RefreshSideListContext)
	const selectedRecordId = isNewRecord ? null : recordId
	const [t] = useTranslation()
	const locales = useLocale()
	const [firstRecordId, setFirstRecordId] = useState(null)
	const [activeFilter, setFilterActive] = useState(false)
	const dataList = listQueryRef?.current?.data?.cashAssist?.listProducts?.items
	const pathByTab = getNameOfGqlQuery(sectionData.listQuery)
	const pageData = listQueryRef?.current?.data?.cashAssist?.[pathByTab]?.items

	useEffect(() => {
		if(firstRecordId && activeFilter) {
			const redirectPath = getRouteById(firstRecordId)
			if(redirectPath) {
				history.push(redirectPath)
				setFirstRecordId(null)
				setFilterActive(false)
			}
			return
		}
		if(!recordId && firstRecordId) {
			history.push(getRouteById(firstRecordId))
		}
	}, [firstRecordId])
	const redirectIfNull = getRouteById(recordId).split('/')
	useEffect(() => {
		if(pageData && redirectIfNull[redirectIfNull.length - 1] === 'null') {
			pageData[0] && history.push(pageData[0]?.id)
		}
	}, [pageData])
	const totalRecords = multiEdit?.recordIds.length
	const totalForTab = listQueryRef.current?.data?.cashAssist?.[pathByTab]?.total

	const [countForTab, setCount] = useState(totalForTab)
	useEffect(() => {
		if(status === 'RECORD_ADDED') {
			if(totalForTab) {
				setCount(totalForTab + 1)
			}
		}
	}, [status])

	useEffect(() => {
		setCount(listQueryRef.current?.data?.cashAssist?.[pathByTab]?.total)
	}, [listQueryRef.current, filters])

	const generateCountByPath = () => {
		const total = (<SideListLabel
			label={sectionData?.label}
			recordsCounter={sectionData?.recordsCounter}
			selectionCounter={multiEdit.isActive ? (multiEdit.isAllSelected ? countForTab : totalRecords) : ''}
			totalForDifferentTab={countForTab}
		/>)
		return total
	}

	let columns = sectionData.columns
	if(typeof columns === 'function') {
		columns = columns(t, locales, { commonData: commonData || listQueryRef?.current?.data })
	}
	if (multiEdit.isActive) {
		columns = [
			{
				Header: sectionData?.sideListProps?.useTotalCheckbox ? <StyledCheckbox className={'check-all'} noForm checked={multiEdit.isAllSelected} onChange={() => {
					multiEdit.setRecordIds([])
					multiEdit.setIsAllSelected(prev => !prev)
				}} /> : null,
				sorting: false,
				Cell: ({ original }) => <Checkbox noForm readOnly checked={multiEdit.isAllSelected || multiEdit.recordIds.indexOf(original.id) !== -1} />,
				sortable: false,
				resizable: false,
				width: 44
			},
			...columns
		]
	}

	const handleRowClick = (tableProps, row) => {
		if (multiEdit.isActive) {
			multiEdit.setIsAllSelected(!multiEdit.recordIds.includes(row.original.id) ? totalRecords + 1 === countForTab : false)

			return multiEdit.handleSelectRecord(row.original.id)
		}
	}
	const handleScrollStartIndex = (data) => {
		if(!isEmpty(data) && !isEqual(data, dataList)) {
			setFirstRecordId(data[0].id)
		}
		return data.findIndex(({ id }) => id === selectedRecordId)
	}

	return (
		<StyledColList
			xs={xs}
			className={classNames({ [styles.fixedCol]: fixSidebar })}
			width={width}
			wide={isWideCol}
		>
			<DataTable
				key={section}
				withSearch
				selectRow={{
					selected: selectedRecordId,
					setSelectedRecord: selectRecord
				}}
				onRowClick={handleRowClick}
				columns={columns}
				{...!props?.navBarProps?.hideLabel && { label: generateCountByPath() }}
				rowMenu={[
					{ func: startCopyRow, Icon: Copy },
					...(mutations.deleteItem ? [{ func: mutations.deleteItem, Icon: Trashbin }] : [])
				]}
				cssHeight={true}
				itemSize={itemSize}
				getScrollStartIndex={handleScrollStartIndex}
				remoteOptions={{
					activeFilter,
					onFilterActive: setFilterActive,
					queryRef: listQueryRef,
					query: sectionData.listQuery,
					withSort: true,
					startFromId: state.isRedirected ? null : selectedRecordId,
					fetchPolicy: listFetchPolicy || 'cache-and-network',
					withFilter: {
						FilterComponent: sectionData.FilterComponent,
						filterValues: {
							...sectionData?.initialQueryVariables?.filter,
							...filters[section]
						},
						onFilter: filterVal => setFilter(section, filterVal)
					},
					refreshData: refreshSideList,
					onDataRefreshed: () => setRefreshSideList(false),
					clearStartFromId: status === 'RECORD_ADDED'
				}}
				resizable={true}
				onAddRecord={startNewRecord ? startNewRecord : null}
				{...datatableProps}
			/>
		</StyledColList>
	)
}

export default SideList
